import React, { useCallback } from 'react';
import MessageContent from './MessageContent';
import {
  GECKO_UTILS,
  GeckoAvatar,
  GeckoDropdown,
  GeckoModalService,
  GeckoModal,
} from 'ui';
import CONSTS from 'constants/index.js';
import PresenceBadge from 'components/PresenceBadge';
import DisplayListButton from 'components/DisplayListButton';
import SuggestAnswerButton from 'components/SuggestAnswerButton';
import AttachmentPreview from '../AttachmentPreview';
import {
  DeletedMessageMetadata,
  SystemInformationMessageMetadata,
  MessageMetadata,
} from './Metadata';
import './Message.scss';

const MessageComponent = React.forwardRef(
  (
    {
      onAvatarClick,
      senderPresence,
      sender,
      isSystemMessage,
      isInstagramMessage,
      isDeletedMessage,
      style,
      message,
      displayedChannel,
      messageIsEmail,
      getResendOptions,
      messageIsDisplayListSkill,
      messageReady,
      messageIsSuggestAnswerSkill,
      onMessageClick,
      hideMetadata,
      isAgent,
      isInboundEmail,
      isNote,
      messageIsBotResponse,
    },
    ref
  ) => {
    const styles = {
      container: { ...style.container },
      messageContainer: {
        ...style.messageContainer,
        cursor: onMessageClick ? 'pointer' : 'inherit',
        overflowWrap: 'break-word',
        wordWrap: 'break-word',
        wordBreak: 'break-word',
      },
      content: { ...style.content },
      meta: { ...style.meta },
    };
    const failedMessage = message.status === CONSTS.MESSAGE_STATUS_FAILED;
    const getErrorMessage = () => {
      if (failedMessage) {
        if (message.error_message) {
          return message.error_message;
        }
        return CONSTS.MESSAGE_STATUS_DESCRIPTIONS[message.status];
      }
      return;
    };
    const toolTipText =
      getErrorMessage() ||
      CONSTS.MESSAGE_STATUS_DESCRIPTIONS[message.status] ||
      `Unknown message status: ${message.status}`;

    const classNames = GECKO_UTILS.classNames({
      ConversationMessage: true,
      agent: isAgent,
      'inbound-email': isInboundEmail,
      note: isNote,
      'system-message': isSystemMessage,
      failed: message.status === CONSTS.MESSAGE_STATUS_FAILED,
      'temporary-failed':
        message.status === CONSTS.MESSAGE_STATUS_TEMPORARY_FAIL,
    });

    const showImage = useCallback((attachment, fileName) => {
      new GeckoModalService().open(() => {
        return (
          <GeckoModal
            name={`View File`}
            children={<img src={attachment} alt={fileName} />}
          />
        );
      });
    }, []);

    const avatar = (
      <div className="conversation-message-avatar" onClick={onAvatarClick}>
        <PresenceBadge presence={senderPresence}>
          <GeckoAvatar
            name={
              sender.full_name || `${sender.first_name} ${sender.last_name}`
            }
            first_name={sender.first_name}
            last_name={sender.last_name}
            color={sender.additional.avatar_colour}
            bgImage={sender.additional.profile_picture_url}
          />
        </PresenceBadge>
      </div>
    );

    const renderMetadata = () => {
      if (isDeletedMessage === true) {
        return (
          <div className="row">
            <div className="col">
              <DeletedMessageMetadata message={message} />
            </div>
          </div>
        );
      }
      if (!isSystemMessage) {
        return (
          <div className="row">
            <div className="col">
              <MessageMetadata
                message={message}
                displayedChannel={displayedChannel}
                messageIsEmail={messageIsEmail}
                messageIsBotResponse={messageIsBotResponse}
              />
            </div>
          </div>
        );
      }
      return (
        <div className="row">
          <div className="col">
            <SystemInformationMessageMetadata message={message} />
          </div>
        </div>
      );
    };

    const renderSkillOptions = () => {
      if (messageIsSuggestAnswerSkill) {
        return (
          <div className="suggest-answer">
            {message.config.answers.map((answer, index) => {
              return (
                <SuggestAnswerButton
                  key={`suggest-answer-button-${index}`}
                  name={answer.label}
                />
              );
            })}
          </div>
        );
      }
      if (messageIsDisplayListSkill) {
        return (
          <div className="display-list">
            {message.config.answers.map((answer, index) => {
              return (
                <DisplayListButton
                  key={`display-list-button-${index}`}
                  name={answer.label}
                />
              );
            })}
          </div>
        );
      }
      return null;
    };

    const onAttachmentClick = useCallback(
      (url, contentType, fileName) => {
        if (isInstagramMessage || contentType.includes('image')) {
          showImage(url, fileName);
        }
      },
      [isInstagramMessage, showImage]
    );

    return (
      <div className={classNames} style={styles.container} ref={ref}>
        <div className="row">
          {avatar}
          <div
            className="conversation-message-container col-10"
            style={styles.messageContainer}
            onClick={onMessageClick}
          >
            <div className="ConversationMessage-wrapper">
              {message.status === CONSTS.MESSAGE_STATUS_FAILED ? (
                <div
                  className={`conversation-message-content-resend resend-${message.id}`}
                  onClick={(event) => event.stopPropagation()}
                >
                  <GeckoDropdown
                    bind={message}
                    className="resend-dropdown"
                    preset="actions_compact"
                    align="left"
                    name=""
                    index={message.id}
                    icon="fas fa-redo-alt"
                    size="sm"
                    items={getResendOptions(message)}
                  />
                </div>
              ) : null}
              <div
                className={`conversation-message-content ${
                  messageIsDisplayListSkill ? 'full-width' : ''
                }`}
                style={styles.content}
                onLoad={messageReady}
              >
                {isDeletedMessage ? (
                  <span className="MessageContent">
                    <i className="far fa-message-slash"></i> {message.content}
                  </span>
                ) : (
                  <MessageContent
                    id={message.id}
                    content={message.content === '' ? ' ' : message.content}
                    editor_object={message.editor_object}
                    html={message.html}
                    fullContent={
                      message.metadata && message.metadata.fullContent
                    }
                    onAttachmentClick={onAttachmentClick}
                  />
                )}
                {(message.attachments || []).map((attachment) => (
                  <AttachmentPreview
                    onClick={onAttachmentClick}
                    key={`mesg_att_${attachment}`}
                    attachment={attachment}
                    isInstagramMessage={isInstagramMessage}
                  />
                ))}
              </div>
            </div>
            {renderSkillOptions()}
            {hideMetadata ? null : renderMetadata()}
          </div>
        </div>
      </div>
    );
  }
);

export default MessageComponent;
